import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowRightArrowLeft, faArrowUp, faBolt, faCalendar, faHeadset, faPhone, faUser } from '@fortawesome/free-solid-svg-icons';
import { FilterChips, RangeSelect, ScopeNote, SearchInput, Toolbar } from '../../components';
import { HOME_RANGES } from '../../hooks/useHomeScope';
import { usePhone } from '../../hooks/usePhone';
import { P, onPhone, scrollRow } from '../../theme/tokens';

const Bar = styled(Toolbar)`
  ${onPhone} {
    gap: 8px;
    margin-bottom: 12px;
    > label {
      flex: 1 1 100%;
      min-width: 0;
      order: -1;
      min-height: 40px;
      padding: 0 14px;
    }
  }
`;

const Segs = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
  order: -1;
  ${scrollRow}
  > * {
    flex: none;
  }
  > label {
    min-height: 36px;
    padding: 0 12px;
  }
`;

const Seg = styled.div`
  display: inline-flex;
  border: 1px solid ${P.gray200};
  border-radius: 8px;
  overflow: hidden;
  background: ${P.white};

  button {
    border: 0;
    background: none;
    padding: 0 10px;
    min-height: 36px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: ${P.muted};
    cursor: pointer;
  }
  button[aria-pressed='true'] {
    background: ${P.navy};
    color: ${P.white};
  }
`;

const SegControl = ({ label, options, value, onChange }) => (
  <Seg role="group" aria-label={label}>
    {options.map((o) => (
      <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
        {o.icon && <FontAwesomeIcon icon={o.icon} />}
        {o.short}
      </button>
    ))}
  </Seg>
);

const Selects = styled.div`
  display: contents;
`;

export const TYPE_FILTERS = [
  { key: 'all', label: 'All Types' },
  { key: 'leasing', label: 'Leasing' },
  { key: 'owner', label: 'Owner' },
  { key: 'hvac', label: 'HVAC' },
  { key: 'maintenance', label: 'Maintenance' },
  { key: 'emergency', label: 'Emergency' }
];

export const RANGES = [
  { value: 'today', label: 'Today' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: 'all', label: 'All time' }
];

export const DIRECTIONS = [
  { value: 'all', label: 'All directions', short: 'All', icon: faPhone },
  { value: 'inbound', label: 'Inbound', short: 'In', icon: faArrowDown },
  { value: 'outbound', label: 'Outbound', short: 'Out', icon: faArrowUp }
];

const handledByOptions = (assistantName) => [
  { value: 'all', label: 'Anyone', short: 'All' },
  { value: 'ai', label: assistantName, short: assistantName, icon: faBolt },
  { value: 'team', label: 'Team', short: 'Team', icon: faUser }
];

const rangeOptions = (range) => {
  const inherited = HOME_RANGES.find((r) => r.key === range);
  return RANGES.some((r) => r.value === range) || !inherited ? RANGES : [...RANGES, { value: range, label: inherited.label }];
};

export default function CallsToolbar({
  type,
  onType,
  search,
  onSearch,
  range,
  onRange,
  direction,
  onDirection,
  handledBy,
  onHandledBy,
  assistantName
}) {
  const phone = usePhone();
  const rangeSelect = (
    <RangeSelect>
      <FontAwesomeIcon icon={faCalendar} />
      <select aria-label="Date range" value={range} onChange={(e) => onRange(e.target.value)}>
        {rangeOptions(range).map((r) => (
          <option key={r.value} value={r.value}>
            {r.label}
          </option>
        ))}
      </select>
    </RangeSelect>
  );
  return (
    <Bar>
      <FilterChips label="Call type" options={TYPE_FILTERS} value={type} onChange={onType} />
      <SearchInput value={search} onChange={onSearch} placeholder={phone ? 'Search name, number, property' : 'Search calls…'} hint={phone ? undefined : '/'} />
      {phone && (
        <Segs>
          <SegControl label="Direction" options={DIRECTIONS} value={direction} onChange={onDirection} />
          <SegControl label="Handled by" options={handledByOptions(assistantName)} value={handledBy} onChange={onHandledBy} />
          {rangeSelect}
        </Segs>
      )}
      {!phone && (
        <Selects>
          {rangeSelect}
          <RangeSelect>
            <FontAwesomeIcon icon={faArrowRightArrowLeft} />
            <select aria-label="Direction" value={direction} onChange={(e) => onDirection(e.target.value)}>
              {DIRECTIONS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </RangeSelect>
          <RangeSelect>
            <FontAwesomeIcon icon={faHeadset} />
            <select aria-label="Handled by" value={handledBy} onChange={(e) => onHandledBy(e.target.value)}>
              {handledByOptions(assistantName).map((h) => (
                <option key={h.value} value={h.value}>
                  {h.label}
                </option>
              ))}
            </select>
          </RangeSelect>
        </Selects>
      )}
      <ScopeNote />
    </Bar>
  );
}
